import { z } from 'zod';

export interface GeocodingResult {
  latitude: number;
  longitude: number;
  displayName: string;
}

interface CachedGeocodingResult extends GeocodingResult {
  timestamp: number;
}

// Schema for validating cached geocoding results
const CachedGeocodingResultSchema = z.object({
  latitude: z.number(),
  longitude: z.number(),
  displayName: z.string(),
  timestamp: z.number(),
});

const GeocodingCacheSchema = z.record(z.string(), CachedGeocodingResultSchema);

const CACHE_KEY = 'geocoding_cache';
const CACHE_EXPIRY_DAYS = 30;
const MAX_CACHE_SIZE = 1024 * 1024; // 1MB limit

/**
 * Cache persistente no localStorage
 */
class GeocodingCache {
  private cache: Map<string, CachedGeocodingResult>;

  constructor() {
    this.cache = new Map();
    this.loadFromLocalStorage();
  }

  private loadFromLocalStorage() {
    try {
      const stored = localStorage.getItem(CACHE_KEY);
      if (!stored) return;
      
      // Check size limit
      if (stored.length > MAX_CACHE_SIZE) {
        console.warn('Geocoding cache too large, clearing');
        localStorage.removeItem(CACHE_KEY);
        return;
      }
      
      const parsed = JSON.parse(stored);
      const validated = GeocodingCacheSchema.safeParse(parsed);
      
      if (validated.success) {
        Object.entries(validated.data).forEach(([key, value]) => {
          this.cache.set(key, value as CachedGeocodingResult);
        });
        console.log(`📦 Cache carregado: ${this.cache.size} endereços`);
      } else {
        console.warn('Invalid geocoding cache structure, clearing');
        localStorage.removeItem(CACHE_KEY);
      }
    } catch (error) {
      console.warn('Erro ao carregar cache:', error);
      localStorage.removeItem(CACHE_KEY);
    }
  }

  private saveToLocalStorage() {
    try {
      const data: Record<string, CachedGeocodingResult> = {};
      this.cache.forEach((value, key) => {
        data[key] = value;
      });
      localStorage.setItem(CACHE_KEY, JSON.stringify(data));
    } catch (error) {
      console.warn('Erro ao salvar cache:', error);
    }
  }

  get(key: string): GeocodingResult | null {
    const cached = this.cache.get(key);
    if (!cached) return null;

    // Verificar se expirou
    const age = Date.now() - cached.timestamp;
    const maxAge = CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
    
    if (age > maxAge) {
      this.cache.delete(key);
      this.saveToLocalStorage();
      return null;
    }

    return {
      latitude: cached.latitude,
      longitude: cached.longitude,
      displayName: cached.displayName,
    };
  }

  set(key: string, result: GeocodingResult) {
    this.cache.set(key, {
      ...result,
      timestamp: Date.now(),
    });
    this.saveToLocalStorage();
  }

  has(key: string): boolean {
    return this.get(key) !== null;
  }
}

const cache = new GeocodingCache();

/**
 * Normaliza texto removendo acentos e caracteres especiais
 */
function normalizeText(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove acentos
    .replace(/[^\w\s]/gi, ' ') // Remove caracteres especiais
    .replace(/\s+/g, ' ') // Normaliza espaços
    .trim();
}

/**
 * Formata endereço removendo partes vazias
 */
function formatAddress(
  street: string,
  number: string,
  neighborhood: string,
  city: string,
  state: string,
  normalize: boolean = false
): string {
  let parts = [street, number, neighborhood, city, state, 'Brasil'].filter(
    (part) => part && part.trim() !== ''
  );
  
  // Normalizar se solicitado (para melhorar geocoding de nomes não-brasileiros)
  if (normalize) {
    parts = parts.map(normalizeText);
  }
  
  return parts.join(', ');
}

/**
 * Geocodifica um endereço usando Nominatim (OpenStreetMap)
 * @param street Logradouro
 * @param number Número
 * @param neighborhood Bairro
 * @param city Cidade
 * @param state Estado
 * @returns Coordenadas (lat/lng) ou null se não encontrado
 */
export async function geocodeAddress(
  street: string,
  number: string,
  neighborhood: string,
  city: string,
  state: string
): Promise<GeocodingResult | null> {
  try {
    // Formatar endereço completo
    const fullAddress = formatAddress(street, number, neighborhood, city, state);
    const encodedAddress = encodeURIComponent(fullAddress);
    
    // Usar Nominatim (OpenStreetMap) com timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5s timeout
    
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?` +
      `q=${encodedAddress}&format=json&limit=1&countrycodes=br`,
      {
        headers: {
          'User-Agent': 'ImobiliariaApp/1.0' // Nominatim requer User-Agent
        },
        signal: controller.signal,
      }
    );
    
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      console.error('Erro no geocoding:', response.statusText);
      return null;
    }
    
    const data = await response.json();
    
    if (data && data.length > 0) {
      const result = data[0];
      return {
        latitude: parseFloat(result.lat),
        longitude: parseFloat(result.lon),
        displayName: result.display_name
      };
    }
    
    // Fallback 1: tentar com endereço normalizado (sem acentos)
    const normalizedAddress = formatAddress(street, number, neighborhood, city, state, true);
    const normalizedEncoded = encodeURIComponent(normalizedAddress);
    
    const normalizedResponse = await fetch(
      `https://nominatim.openstreetmap.org/search?` +
      `q=${normalizedEncoded}&format=json&limit=1&countrycodes=br`,
      {
        headers: { 'User-Agent': 'ImobiliariaApp/1.0' },
      }
    );
    
    if (normalizedResponse.ok) {
      const normalizedData = await normalizedResponse.json();
      if (normalizedData && normalizedData.length > 0) {
        const result = normalizedData[0];
        console.log(`✅ Normalizado sucesso: ${neighborhood}, ${city}`);
        return {
          latitude: parseFloat(result.lat),
          longitude: parseFloat(result.lon),
          displayName: result.display_name
        };
      }
    }
    
    // Fallback 2: tentar apenas com cidade + bairro
    if (city && neighborhood) {
      const simpleAddress = `${neighborhood}, ${city}, ${state}, Brasil`;
      const simpleEncoded = encodeURIComponent(simpleAddress);
      
      const fallbackResponse = await fetch(
        `https://nominatim.openstreetmap.org/search?` +
        `q=${simpleEncoded}&format=json&limit=1&countrycodes=br`,
        {
          headers: { 'User-Agent': 'ImobiliariaApp/1.0' },
        }
      );
      
      if (fallbackResponse.ok) {
        const fallbackData = await fallbackResponse.json();
        if (fallbackData && fallbackData.length > 0) {
          const result = fallbackData[0];
          console.log(`✅ Fallback bairro+cidade: ${neighborhood}, ${city}`);
          return {
            latitude: parseFloat(result.lat),
            longitude: parseFloat(result.lon),
            displayName: result.display_name
          };
        }
      }
    }
    
    // Fallback 3: tentar apenas com a cidade
    if (city) {
      const cityAddress = `${city}, ${state}, Brasil`;
      const cityEncoded = encodeURIComponent(cityAddress);
      
      const cityResponse = await fetch(
        `https://nominatim.openstreetmap.org/search?` +
        `q=${cityEncoded}&format=json&limit=1&countrycodes=br`,
        {
          headers: { 'User-Agent': 'ImobiliariaApp/1.0' },
        }
      );
      
      if (cityResponse.ok) {
        const cityData = await cityResponse.json();
        if (cityData && cityData.length > 0) {
          const result = cityData[0];
          console.log(`✅ Fallback cidade: ${city}`);
          return {
            latitude: parseFloat(result.lat),
            longitude: parseFloat(result.lon),
            displayName: result.display_name
          };
        }
      }
    }
    
    console.warn('Endereço não encontrado:', fullAddress);
    return null;
    
  } catch (error) {
    if ((error as Error).name === 'AbortError') {
      console.warn('Geocoding timeout:', formatAddress(street, number, neighborhood, city, state));
    } else {
      console.error('Erro ao geocodificar endereço:', error);
    }
    return null;
  }
}

/**
 * Geocoding com cache persistente
 */
export async function geocodeAddressWithCache(
  street: string,
  number: string,
  neighborhood: string,
  city: string,
  state: string
): Promise<GeocodingResult | null> {
  const cacheKey = `${street}-${number}-${neighborhood}-${city}-${state}`;
  
  // Verificar cache
  const cached = cache.get(cacheKey);
  if (cached) {
    console.log('💾 Cache hit:', neighborhood);
    return cached;
  }
  
  // Geocodificar
  const result = await geocodeAddress(street, number, neighborhood, city, state);
  
  // Salvar no cache (mesmo se null)
  if (result) {
    cache.set(cacheKey, result);
  }
  
  return result;
}

/**
 * Utilitário para dividir array em chunks
 */
export function chunk<T>(array: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
}
